import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import type { ActivityLogItem } from '../../store/game.store';

interface LeftPanelProps {
  currentNumber: number | null;
  previousNumber: number | null;
  calledNumbers: number[];
  activityLog: ActivityLogItem[];
  currentNumberTime: number;
}

export function LeftPanel({
  currentNumber,
  previousNumber,
  calledNumbers,
  activityLog,
  currentNumberTime,
}: LeftPanelProps) {
  const [timeLeft, setTimeLeft] = useState(0);

  // Progressive countdown sync loop (runs at 60fps/100ms resolution for ultra-smooth rendering)
  useEffect(() => {
    if (currentNumber === null || !currentNumberTime) {
      setTimeLeft(0);
      return;
    }

    const updateTimer = () => {
      const elapsed = (Date.now() - currentNumberTime) / 1000;
      const remaining = Math.max(0, 10 - elapsed);
      setTimeLeft(remaining);
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 50);
    return () => clearInterval(intervalId);
  }, [currentNumber, currentNumberTime]);

  // SVG progressive ring details
  const radius = 28;
  const strokeWidth = 3;
  const circumference = 2 * Math.PI * radius; // ~175.93
  const strokeDashoffset = circumference - (timeLeft / 10) * circumference;

  return (
    <div className="glass rounded-3xl p-4 border border-white/10 shadow-glow-lg flex flex-col justify-between h-full text-left overflow-hidden">
      <div className="space-y-4">
        {/* Top: Current Number Card */}
        <div className="glass-light p-4 rounded-2xl border border-white/5 relative flex items-center justify-between overflow-hidden shadow-inner select-none">
          <div className="space-y-1">
            <span className="block text-[9px] uppercase tracking-wider text-surface-400 font-bold">
              Current Number
            </span>
            <div className="flex items-baseline space-x-2">
              <span className="text-[10px] text-surface-500 font-bold uppercase">Prev:</span>
              <span className="text-xs font-black font-mono text-white/60">
                {previousNumber !== null ? previousNumber.toString().padStart(2, '0') : '--'}
              </span>
            </div>
          </div>

          {/* Ball with Progressive SVG Ring wrapper */}
          <div className="relative w-16 h-16 flex items-center justify-center">
            {/* Countdown SVG Progressive circle */}
            {currentNumber !== null && (
              <svg className="absolute transform -rotate-95 w-16 h-16 pointer-events-none z-10">
                <circle
                  cx="32"
                  cy="32"
                  r={radius}
                  stroke="rgba(255,255,255,0.05)"
                  strokeWidth={strokeWidth}
                  fill="transparent"
                />
                <motion.circle
                  cx="32"
                  cy="32"
                  r={radius}
                  stroke={timeLeft > 3 ? '#ec4899' : '#ef4444'} // turns red when time gets low
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  strokeDasharray={circumference}
                  animate={{ strokeDashoffset }}
                  transition={{ ease: 'linear', duration: 0.05 }}
                  strokeLinecap="round"
                />
              </svg>
            )}

            {/* Current Ball */}
            <motion.div
              key={currentNumber}
              initial={{ scale: 0.8, rotate: -15, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              style={{
                background: currentNumber
                  ? 'radial-gradient(circle at 30% 30%, #ffe082 0%, #ffb300 50%, #b78103 100%)'
                  : 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)',
                boxShadow: currentNumber
                  ? '0 4px 12px rgba(255, 179, 0, 0.3), inset 0 -3px 8px rgba(0,0,0,0.6)'
                  : 'none',
              }}
              className="w-12 h-12 rounded-full flex items-center justify-center border border-amber-400/20 z-0"
            >
              {currentNumber !== null ? (
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-inner">
                  <span className="font-display font-black text-slate-800 text-xs font-mono">
                    {currentNumber.toString().padStart(2, '0')}
                  </span>
                </div>
              ) : (
                <span className="text-surface-600 text-sm font-black font-mono">--</span>
              )}
            </motion.div>
          </div>
        </div>

        {/* Middle: Called Numbers Grid */}
        <div className="space-y-1.5 flex-1 flex flex-col">
          <label className="text-[9px] font-bold uppercase tracking-wider text-surface-400 block">
            Called History ({calledNumbers.length} / 90)
          </label>
          <div className="grid grid-cols-10 gap-1 p-2 rounded-xl bg-slate-950/40 border border-white/5 overflow-y-auto max-h-[140px] md:max-h-[220px]">
            {Array.from({ length: 90 }, (_, i) => {
              const num = i + 1;
              const isCalled = calledNumbers.includes(num);
              const isLatest = currentNumber === num;

              return (
                <div
                  key={num}
                  style={{
                    boxShadow: isLatest ? '0 0 8px rgba(255,179,0,0.5)' : 'none',
                  }}
                  className={`aspect-square flex items-center justify-center rounded text-[8px] font-mono font-black border transition-all duration-300 ${
                    isLatest
                      ? 'bg-amber-500 border-amber-400 text-slate-900 scale-105'
                      : isCalled
                        ? 'bg-purple-600/20 border-purple-500/20 text-purple-300'
                        : 'bg-transparent border-white/2 text-surface-600'
                  }`}
                >
                  {num.toString().padStart(2, '0')}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom: Activity Feed */}
      <div className="space-y-1.5 pt-3 border-t border-white/5 mt-4">
        <label className="text-[9px] font-bold uppercase tracking-wider text-surface-500 block">
          Activity Feed
        </label>
        <div className="space-y-1 max-h-[90px] overflow-hidden">
          {activityLog.slice(0, 5).map((log) => (
            <div key={log.id} className="flex justify-between items-center text-[9px] font-semibold text-surface-400 font-mono tracking-wide py-0.5 border-b border-white/2">
              <span className="truncate max-w-[190px]">{log.text}</span>
              <span className="text-surface-600 pl-2">{log.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
export default LeftPanel;
